import React from 'react';
import { FmCheckbox } from './FmCheckbox';
import { FmNumberField } from './FmNumberField';
import { FmSelect } from './FmSelect';
import { FmTextField } from './FmTextField';
import type {
  FmFormFlowEditableCellEditorDescriptor,
  FmFormFlowScalar,
} from './FmFormFlowPage.types';

interface FmFormFlowEditableCellProps {
  id: string;
  ariaLabel: string;
  testId: string;
  editor: FmFormFlowEditableCellEditorDescriptor;
  value: FmFormFlowScalar;
  onChange: (value: FmFormFlowScalar) => void;
}

/** Renderer-owned scalar editor used by every editable form-flow collection. */
export function FmFormFlowEditableCell({
  id,
  ariaLabel,
  testId,
  editor,
  value,
  onChange,
}: FmFormFlowEditableCellProps): React.ReactElement {
  switch (editor.kind) {
    case 'text':
      return (
        <FmTextField
          id={id}
          ariaLabel={ariaLabel}
          value={typeof value === 'string' ? value : ''}
          placeholder={editor.placeholder}
          maxLength={editor.maxLength}
          data-testid={testId}
          onChange={(event) => onChange(event.target.value)}
        />
      );
    case 'number':
      return (
        <FmNumberField
          id={id}
          value={typeof value === 'number' ? value : null}
          placeholder={editor.placeholder}
          min={editor.min}
          max={editor.max}
          step={editor.step}
          precision={editor.precision}
          full
          data-testid={testId}
          onChange={onChange}
        />
      );
    case 'select':
      return (
        <FmSelect<string | number | null>
          id={id}
          ariaLabel={ariaLabel}
          value={typeof value === 'string' || typeof value === 'number' ? value : null}
          placeholder={editor.placeholder}
          allowClear={editor.allowClear}
          controlWidth="fill"
          data-testid={testId}
          options={editor.options.map((option) => ({
            label: option.label,
            value: option.value,
            disabled: option.disabled,
          }))}
          onChange={(nextValue) => onChange(nextValue ?? null)}
        />
      );
    case 'boolean':
      return (
        <FmCheckbox
          id={id}
          checked={value === true}
          ariaLabel={ariaLabel}
          data-testid={testId}
          onChange={onChange}
        />
      );
  }
}
